/**
 * Client-side state that belongs to ONE logged-in merchant.
 *
 * The app caches per-merchant things in localStorage — the logged-in user, and the location each screen
 * was last filtering by (dashboardLocationId, controlPanelLocationId, orders_selected_location,
 * analyticsLocationId, …). Logging out used to remove only `user`, so everything else survived into the
 * next session. Log in as another merchant and the app started up holding the previous tenant's
 * location ids: screens that validate them recover (they fall back to "all" or the first location),
 * screens that don't end up querying a location the new merchant does not own — which comes back empty
 * and looks exactly like "the page didn't reload".
 *
 * It is also residue from one tenant sitting in another tenant's session, which is not something this
 * platform should leave lying around whatever the visible symptom.
 *
 * Rather than list the keys to delete — the list that was already out of date once — this clears
 * everything except an explicit allowlist of tenant-NEUTRAL preferences. A new per-merchant key is then
 * cleaned up by default, and only a deliberate addition here survives a tenant switch.
 */

import { isPosWebviewSession } from '@/services/auth/AuthHeader';

/** Keys that describe the person's UI preference, not the merchant's data. These survive a logout. */
const TENANT_NEUTRAL_KEYS = new Set([
  'bo_products_compact',  // compact/detailed product list — a display choice, identical for any merchant
  // Whether this person has been through the guided tour. Without it here the flag was wiped on every
  // logout and the walkthrough opened itself at every single login — an introduction that will not stop
  // introducing itself is worse than none. It describes the person, not the tenant: someone who has seen
  // the menu explained has seen it, whichever merchant they sign in as next.
  'bo_training_seen'
]);

export function clearMerchantScopedState() {
  // The TastyPOS refund WebView authenticates with a credential the APP injected into localStorage. It
  // has no login screen and no way to put it back, so clearing it does not sign someone out — it strands
  // a terminal mid-refund on a login page it can never satisfy.
  if (isPosWebviewSession()) {
    console.warn('POS WebView session: keeping the injected credential.');
    return;
  }
  try {
    Object.keys(localStorage)
      .filter(key => !TENANT_NEUTRAL_KEYS.has(key))
      .forEach(key => localStorage.removeItem(key));
  } catch (e) {
    // localStorage can throw (private mode, quota, disabled). Never let it break sign-out.
    console.warn('Could not clear merchant-scoped client state:', e);
  }
}

export default { clearMerchantScopedState };
